import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { BehaviorSubject, throwError, timeout } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

const REQUEST_TIMEOUT_MS = 30_000;
const PUBLIC_ROUTES = ['/assets/', '/home'];

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Functional HTTP interceptor — Angular 20 pattern.
 * Attaches Bearer token, handles 401 refresh, portal-aware redirect, 30s timeout.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (PUBLIC_ROUTES.some(route => req.url.includes(route))) {
    return next(req);
  }

  // inject() must be called at the top level of the interceptor function
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const token = authService.getAccessToken();
  if (token) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    timeout(REQUEST_TIMEOUT_MS),
    catchError((error: HttpErrorResponse | Error) => {
      if (error.name === 'TimeoutError') {
        toastService.error('Request timed out. Please try again.');
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          return handle401(req, next, authService, toastService);
        }

        if (!req.context.get(SKIP_ERROR_TOAST)) {
          if (error.status === 429) {
            toastService.error('Too many requests. Please try again later.');
          } else if (error.status >= 400 && error.status < 500) {
            // Client errors (400, 403, 404, 409, 422, etc.) — show the API message to the user.
            const body = error.error;
            if (Array.isArray(body?.errors) && body.errors.length > 0) {
              const messages = (body.errors as { message?: string }[])
                .map(e => e.message)
                .filter((m): m is string => !!m);
              if (messages.length > 0) {
                toastService.errorList(messages);
              } else {
                const { title, detail } = extractClientErrorMessage(error);
                toastService.error(title, detail);
              }
            } else {
              const { title, detail } = extractClientErrorMessage(error);
              toastService.error(title, detail);
            }
          } else if (error.status === 502 || error.status === 503) {
            toastService.error('Service temporarily unavailable. Please try again later.');
          } else if (error.status === 504) {
            toastService.error('Request timed out. Please try again.');
          } else if (error.status >= 500) {
            toastService.error('A server error occurred. Please try again later.');
          }
        }
      }

      return throwError(() => error);
    })
  );
};

function handle401(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  toastService: ToastService
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();

    if (refreshToken && !refreshToken.startsWith('refresh-')) {
      return authService.refreshAccessToken(refreshToken).pipe(
        switchMap(response => {
          isRefreshing = false;
          authService.setAccessToken(response.accessToken);
          refreshTokenSubject.next(response.accessToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${response.accessToken}` } }));
        }),
        catchError(err => {
          isRefreshing = false;
          doRedirectToLogin(authService, toastService);
          return throwError(() => err);
        })
      );
    } else {
      isRefreshing = false;
      doRedirectToLogin(authService, toastService);
      return throwError(() => new Error('Session expired'));
    }
  } else {
    return refreshTokenSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap(t => next(req.clone({ setHeaders: { Authorization: `Bearer ${t}` } })))
    );
  }
}

function doRedirectToLogin(authService: AuthService, toastService: ToastService): void {
  authService.clearAuthData();
  toastService.error('Your session has expired. Please log in again.');
  authService.redirectToLogin();
}

/**
 * Extract a user-facing message from a client error (4xx) response.
 * The API is expected to return meaningful messages for client errors
 * (validation failures, not found, forbidden, etc.).
 */
function extractClientErrorMessage(error: HttpErrorResponse): { title: string; detail: string } {
  const body = error.error;

  // RFC 7807 detail field — use as the sole user-facing message; drop the raw HTTP status title
  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return { title: body.detail, detail: '' };
  }

  // Standard message field
  if (typeof body?.message === 'string' && body.message.trim()) {
    return { title: body.message, detail: '' };
  }

  // At least a title from the API
  if (typeof body?.title === 'string' && body.title.trim()) {
    return { title: body.title, detail: '' };
  }

  // Plain string body
  if (typeof body === 'string' && body.trim()) {
    return { title: 'Error', detail: body };
  }

  // Nothing usable in the body — single generic fallback
  return { title: 'Request Failed', detail: `The server returned an error (${error.status}). Please try again.` };
}
